import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type CSSProperties,
  type ReactNode,
} from 'react';

/**
 * ShakeContainer — a wrapper that can jolt (screen-shake) or flash its subtree
 * on demand, driven imperatively via a ref so a game event can punch the frame
 * without a React re-render on every animation frame.
 *
 * The shake is a decaying translate (+ optional slight rotation at high
 * intensity) written as a transform directly on the node in a rAF loop, in the
 * spirit of HoloRoleCard's CSS-var-to-node pattern. The flash is a short opacity
 * pulse of an overlay — it is the vestibular-safe alternative, so under
 * `prefers-reduced-motion` a `shake()` call degrades to `flash()` automatically.
 *
 * Photosensitivity: the flash is a single ~300ms pulse (well under 3Hz) and
 * shakes cannot stack into a strobe (a new shake replaces the running one).
 */

export interface ShakeHandle {
  /** Jolt the frame. `intensity` 0..1 (default 0.6). Reduced-motion → flash. */
  shake(intensity?: number): void;
  /** Single soft opacity pulse. Always safe; used as the reduced-motion shake. */
  flash(color?: string): void;
}

export interface ShakeContainerProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

const SHAKE_MS = 450;
const MAX_TRANSLATE = 12; // px at intensity 1
const MAX_ROTATE = 3; // deg at intensity 1 (only kicks in above the threshold)
const ROTATE_THRESHOLD = 0.6; // rotation only for punchy shakes
const FLASH_MS = 300;

export const ShakeContainer = forwardRef<ShakeHandle, ShakeContainerProps>(function ShakeContainer(
  { children, style, className },
  ref,
) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const flashRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reducedRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedRef.current = mq.matches;
    const h = (e: MediaQueryListEvent) => {
      reducedRef.current = e.matches;
    };
    mq.addEventListener('change', h);
    return () => {
      mq.removeEventListener('change', h);
      cancelAnimationFrame(rafRef.current);
      if (flashTimer.current) clearTimeout(flashTimer.current);
    };
  }, []);

  const runFlash = (color = 'rgba(255,255,255,0.5)') => {
    const el = flashRef.current;
    if (!el) return;
    if (flashTimer.current) clearTimeout(flashTimer.current);
    el.style.background = color;
    // Force a reflow so the transition retriggers even on back-to-back flashes.
    el.style.transition = 'none';
    el.style.opacity = '1';
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    el.offsetHeight;
    el.style.transition = `opacity ${FLASH_MS}ms ease-out`;
    el.style.opacity = '0';
    flashTimer.current = setTimeout(() => {
      el.style.background = 'transparent';
    }, FLASH_MS);
  };

  const runShake = (intensity: number) => {
    const el = nodeRef.current;
    if (!el) return;
    // Reduced motion never translates — it flashes instead.
    if (reducedRef.current) {
      runFlash();
      return;
    }
    const amp = clamp01(intensity);
    const t0 = performance.now();
    cancelAnimationFrame(rafRef.current);

    const tick = (now: number) => {
      const t = (now - t0) / SHAKE_MS;
      if (t >= 1) {
        el.style.transform = '';
        return;
      }
      // Exponential decay envelope; a fast oscillation inside it reads as a jolt.
      const decay = (1 - t) * (1 - t);
      const osc = Math.sin(t * Math.PI * 9);
      const mag = amp * decay;
      const dx = osc * mag * MAX_TRANSLATE;
      const dy = Math.cos(t * Math.PI * 11) * mag * MAX_TRANSLATE * 0.7;
      const rot =
        amp >= ROTATE_THRESHOLD ? osc * mag * MAX_ROTATE * ((amp - ROTATE_THRESHOLD) / (1 - ROTATE_THRESHOLD)) : 0;
      el.style.transform = `translate3d(${dx.toFixed(2)}px, ${dy.toFixed(2)}px, 0) rotate(${rot.toFixed(2)}deg)`;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  useImperativeHandle(
    ref,
    () => ({
      shake: (intensity = 0.6) => runShake(intensity),
      flash: (color?: string) => runFlash(color),
    }),
    [],
  );

  return (
    <div
      ref={nodeRef}
      className={className}
      style={{ position: 'relative', willChange: 'transform', ...style }}
    >
      {children}
      <div
        ref={flashRef}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0,
          background: 'transparent',
          mixBlendMode: 'screen',
          zIndex: 9998,
        }}
      />
    </div>
  );
});

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}
